import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Database } from '../../../lib/database.types';
import { softDelete } from '../../../lib/softDelete';
import { supabase } from '../../../lib/supabase';
import type { CompanyContactRow, ContactInput } from './contacts.schema';

export const contactsQueryKey = (companyId: string | null) => ['contacts', companyId] as const;

function emptyToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

async function fetchContactsByCompany(orgId: string, companyId: string): Promise<CompanyContactRow[]> {
  const { data, error } = await supabase
    .from('contacts')
    .select('id, full_name, role_title, email, phone, linkedin_url, notes, company_id, created_at, updated_at')
    .eq('organization_id', orgId)
    .eq('company_id', companyId)
    .is('deleted_at', null)
    .order('full_name', { ascending: true });

  if (error) throw error;
  return data;
}

export function useContactsByCompany(orgId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: contactsQueryKey(companyId),
    queryFn: () => {
      if (!orgId || !companyId) return Promise.resolve([]);
      return fetchContactsByCompany(orgId, companyId);
    },
    enabled: Boolean(orgId && companyId),
    staleTime: 30 * 1000,
  });
}

type ContactInsert = Database['public']['Tables']['contacts']['Insert'];
type ContactUpdate = Database['public']['Tables']['contacts']['Update'];

function toPayload(values: ContactInput): Omit<ContactInsert, 'organization_id' | 'company_id'> {
  return {
    full_name: values.full_name.trim(),
    role_title: emptyToNull(values.role_title),
    email: emptyToNull(values.email),
    phone: emptyToNull(values.phone),
    linkedin_url: emptyToNull(values.linkedin_url),
    notes: emptyToNull(values.notes),
  };
}

export function useUpsertContact(orgId: string | null, companyId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: ContactInput }) => {
      if (!orgId || !companyId) throw new Error('Missing contact context');

      if (id) {
        const payload: ContactUpdate = toPayload(values);
        const { error } = await supabase.from('contacts').update(payload).eq('id', id);
        if (error) throw error;
        return;
      }

      const payload: ContactInsert = {
        ...toPayload(values),
        organization_id: orgId,
        company_id: companyId,
      };
      const { error } = await supabase.from('contacts').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey(companyId) });
    },
  });
}

export function useSoftDeleteContact(companyId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await softDelete('contacts', id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: contactsQueryKey(companyId) });
    },
  });
}
